/* eslint-disable import-extensions/file-extension-required */
import { graphql } from 'react-relay';
import { NetworkStatus } from 'apollo-client';
import set from 'lodash/set';
import uniqBy from 'lodash/uniqBy';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop/entry-points/ApolloErrorInterop.ts';
import type { GlobalPageCard } from '@confluence/page-card/entry-points/cardTypes.ts';

import type { LoadingState } from '../homeViewsQueryHelpers.ts';
import { getLoadingState, notEmpty, byTitle } from '../homeViewsQueryHelpers.ts';
import { transformStarredToPageCard } from '../pageCardTransformers.ts';

import type {
	useStarredDataHomeStarredPagesQuery$data,
	useStarredDataHomeStarredPagesQuery$variables,
} from './__generated__/useStarredDataHomeStarredPagesQuery.graphql';

const PAGE_SIZE = 24;

export type QueryResult = useStarredDataHomeStarredPagesQuery$data | undefined;
type UseStarredData = ({
	filterText,
	translatedUntitled,
}: {
	filterText: string;
	translatedUntitled: string;
}) => {
	pageCardData: GlobalPageCard[];
	loading: LoadingState;
	networkStatus: NetworkStatus;
	hasNextPage: boolean;
	loadMore: () => void;
	error?: ApolloErrorInterop;
};

const useStarredDataHomeStarredPagesQuery = graphql`
	query useStarredDataHomeStarredPagesQuery($limit: Int, $start: Int) @allowHigherTimeout {
		favoriteContent(limit: $limit, start: $start) {
			nodes {
				id
				subType
				type
				title
				excerpt
				links {
					editui
					webui
				}
				history {
					lastUpdated {
						when
					}
				}
				editorVersion: properties(key: "editor") {
					nodes {
						id
						key
						value
					}
				}
				emoji: properties(keys: ["emoji-title-published"]) {
					nodes {
						id
						key
						value
					}
				}
				metadata {
					currentuser {
						favourited {
							isFavourite
						}
						viewed {
							lastSeen
						}
					}
					likes {
						count
					}
					comments {
						commentsCount
					}
				}
				space {
					alias
					id
					key
					name
				}
			}
			pageInfo {
				hasNextPage
				endCursor
			}
		}
	}
`;

export const useStarredData: UseStarredData = ({ filterText, translatedUntitled }) => {
	const { data, networkStatus, error, fetchMore } = useQueryInterop<
		useStarredDataHomeStarredPagesQuery$data,
		useStarredDataHomeStarredPagesQuery$variables
	>(useStarredDataHomeStarredPagesQuery, {
		variables: {
			limit: PAGE_SIZE,
			start: 0,
		},
		notifyOnNetworkStatusChange: true,
	});
	const hasNextPage = data?.favoriteContent?.pageInfo?.hasNextPage || false;
	const pageCardData = uniqBy(
		(data?.favoriteContent?.nodes || [])
			.filter(notEmpty)
			.map((node) => transformStarredToPageCard(node, translatedUntitled))
			.filter((node) => byTitle(filterText, node.title.text)),
		(pageCard) => pageCard.id,
	);
	const loadMore = () => {
		if (networkStatus === NetworkStatus.ready) {
			void fetchMore({
				variables: {
					start: data?.favoriteContent?.nodes?.length || 0,
				},
				updateQuery: (prev: useStarredDataHomeStarredPagesQuery$data, { fetchMoreResult }) => {
					if (!fetchMoreResult) {
						return prev;
					}
					const prevEdges = prev.favoriteContent?.nodes || [];
					const fetchMoreEdges = fetchMoreResult.favoriteContent?.nodes || [];
					const mergedEdges = [...prevEdges, ...fetchMoreEdges];
					const result = { ...fetchMoreResult };
					set(result, 'favoriteContent.nodes', mergedEdges);
					return result;
				},
			});
		}
	};
	return {
		pageCardData,
		loading: getLoadingState(networkStatus),
		error,
		networkStatus,
		hasNextPage,
		loadMore,
	};
};
